import { qsa } from '../core/dom.js';

// Scroll-reveal. Observes .reveal / .reveal-fade elements and toggles .visible.
//
// Per-container tuning: an ancestor with [data-reveal-margin="0px 0px -6% 0px"]
// gets its own IntersectionObserver with that rootMargin.
// One-shot mode: [data-reveal-once] on <html> or any ancestor disables the
// offscreen reset for the elements it contains.
//
// The safety sweep exists because IO callbacks are async and batched — a fast
// scroll can move an element through the viewport between deliveries and leave
// it stuck hidden. After scrolling settles, anything inside the viewport is
// forced visible.

export function initReveal() {
	document.documentElement.classList.add('anim');

	const els = qsa('.reveal, .reveal-fade');
	if (!els.length) return;

	const show = (entries) => {
		entries.forEach((e) => {
			if (e.isIntersecting) e.target.classList.add('visible');
		});
	};

	const defaultIn = new IntersectionObserver(show, { threshold: 0, rootMargin: '0px 0px 12% 0px' });

	const scopes = qsa('[data-reveal-margin]').map((el) => ({
		el,
		observer: new IntersectionObserver(show, { threshold: 0, rootMargin: el.dataset.revealMargin }),
	}));

	// Reset only when the element is CLEARLY off screen. The positive rootMargin
	// expands the root box so the reset can't race the reveal at the viewport
	// edge during fast scrolling.
	const offscreen = new IntersectionObserver(
		(entries) => {
			entries.forEach((e) => {
				if (!e.isIntersecting) e.target.classList.remove('visible');
			});
		},
		{ threshold: 0, rootMargin: '25% 0px 25% 0px' }
	);

	const once = document.documentElement.hasAttribute('data-reveal-once');

	els.forEach((el) => {
		const scope = scopes.find((s) => s.el.contains(el));
		(scope?.observer ?? defaultIn).observe(el);

		if (!once && !el.closest('[data-reveal-once]')) {
			offscreen.observe(el);
		}
	});

	const ensureInViewVisible = () => {
		const vh = window.innerHeight || document.documentElement.clientHeight;
		els.forEach((el) => {
			if (el.classList.contains('visible')) return;
			const r = el.getBoundingClientRect();
			if (r.bottom > 0 && r.top < vh) {
				el.classList.add('visible');
			}
		});
	};

	let sweepTimer = null;
	const scheduleSweep = () => {
		if (sweepTimer) clearTimeout(sweepTimer);
		sweepTimer = setTimeout(ensureInViewVisible, 140);
	};

	window.addEventListener('scroll', scheduleSweep, { passive: true });
	window.addEventListener('resize', scheduleSweep, { passive: true });
	window.addEventListener('pageshow', ensureInViewVisible);
}
